import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useCategories, useMasterData } from '../masterdata/queries';
import { useAssignableUsers } from '../users/queries';
import type { Ticket } from './api';
import { flattenCategories } from './presentation';
import { useUpdateTicket } from './queries';
import { Select } from '../../components/Select';

interface TicketFieldsFormProps {
  ticket: Ticket;
}

/**
 * Die Felder eines Tickets aendern -- F1-09, F1-11.
 *
 * **`state_id` fehlt hier mit Absicht.** Ein Ticket wird nicht ueber eine
 * Feldaenderung geschlossen, sondern ueber eine benannte Aktion (docs/05,
 * Abschnitt 2). Am Uebergang haengen Vorbedingungen, Protokoll und ab Phase 4
 * die SLA-Uhr -- ein Auswahlfeld liesse all das aus.
 *
 * **`If-Match` traegt die Version mit.** Hat inzwischen jemand anders
 * gespeichert, antwortet der Server mit 409 und schickt den aktuellen Stand
 * mit. Ohne diesen Kopf ueberschriebe der spaetere Speichervorgang den
 * frueheren stillschweigend -- der klassische verlorene Schreibvorgang, den
 * niemand bemerkt, weil beide Seiten eine Erfolgsmeldung sehen.
 */
export function TicketFieldsForm({ ticket }: TicketFieldsFormProps) {
  const { t } = useTranslation();
  const update = useUpdateTicket(ticket.id);

  const queues = useMasterData('queue');
  const types = useMasterData('type');
  const priorities = useMasterData('priority');
  const categories = useCategories();
  const users = useAssignableUsers();

  const [values, setValues] = useState({
    title: ticket.title,
    // Der Editor arbeitet auf dem HTML. Ein Vorgang ohne Auszeichnung
    // -- aus dem Portal oder aus der Zeit vor dem Editor -- bringt
    // seinen Klartext mit; daraus wird beim ersten Speichern ein Absatz.
    description_html: ticket.description_html ?? ticket.description,
    queue_id: ticket.queue.id,
    type_id: ticket.type.id,
    priority_id: ticket.priority.id,
    category_id: ticket.category?.id ?? '',
    owner_id: ticket.owner_id ?? '',
    responsible_id: ticket.responsible_id ?? '',
  });
  const [majorIncident, setMajorIncident] = useState(ticket.is_major_incident);

  const set = (field: keyof typeof values) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const categoryOptions = flattenCategories(categories.data ?? []);

  // Abwesende bleiben in der Liste, tragen aber einen Hinweis. Sie zu
  // entfernen waere falsch: Der Server leitet die Zuweisung auf die
  // Vertretung um (K-70) und vermerkt das am Ticket -- eine Zuweisung an
  // einen Abwesenden ist also zulaessig und hat eine Folge.
  const userOptions = (users.data ?? []).map((user) => ({
    id: user.id,
    label: user.out_of_office_until
      ? `${user.full_name} (${t('tickets.outOfOffice')})`
      : user.full_name,
  }));

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({
          payload: {
            title: values.title,
            description_html: values.description_html,
            queue_id: values.queue_id,
            type_id: values.type_id,
            priority_id: values.priority_id,
            category_id: values.category_id || null,
            is_major_incident: majorIncident,
            owner_id: values.owner_id || null,
            responsible_id: values.responsible_id || null,
          },
          version: ticket.version,
        });
      }}
    >
      <div className="field">
        <label className="field__label" htmlFor="edit-title">
          {t('tickets.subject')}
        </label>
        <input
          id="edit-title"
          className="field__control"
          value={values.title}
          onChange={(event) => {
            set('title')(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="edit-description">
          {t('tickets.description')}
        </label>
        <RichTextEditor
          id="edit-description"
          value={values.description_html}
          onChange={set('description_html')}
          ariaLabel={t('tickets.description')}
          features={{ image: false }}
        />
      </div>

      <Selection
        id="edit-queue"
        label={t('tickets.queue')}
        value={values.queue_id}
        options={(queues.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
        onChange={set('queue_id')}
      />

      <Selection
        id="edit-type"
        label={t('tickets.type')}
        value={values.type_id}
        options={(types.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
        onChange={set('type_id')}
      />

      <Selection
        id="edit-priority"
        label={t('tickets.priority')}
        value={values.priority_id}
        options={(priorities.data ?? []).map((entry) => ({ id: entry.id, label: entry.name }))}
        onChange={set('priority_id')}
      />

      {/* Bearbeiter und fachlich Verantwortlicher sind getrennt (F1-12,
          K-33): Der eine arbeitet, der andere verantwortet das Ergebnis. In
          kleinen Betrieben ist das dieselbe Person -- in groesseren nicht,
          und dann ist die Trennung der Unterschied zwischen "wer macht das"
          und "wen frage ich". */}
      <Selection
        id="edit-owner"
        label={t('tickets.owner')}
        value={values.owner_id}
        options={userOptions}
        emptyLabel={t('tickets.unassigned')}
        onChange={set('owner_id')}
      />

      <Selection
        id="edit-responsible"
        label={t('tickets.responsible')}
        value={values.responsible_id}
        options={userOptions}
        emptyLabel={t('tickets.unassigned')}
        onChange={set('responsible_id')}
      />

      <Selection
        id="edit-category"
        label={t('tickets.category')}
        value={values.category_id}
        options={categoryOptions}
        emptyLabel={t('tickets.noCategory')}
        onChange={set('category_id')}
      />

      {/* Grossstoerung -- F1-27, K-25. Kein eigener Endpunkt, sondern ein Feld
          wie jedes andere: Was eine Grossstoerung ist, entscheidet sich beim
          Bearbeiten des Vorgangs und nicht in einem gesonderten Ablauf.
          Die Folge steht im Server: Ein Sammelticket gibt seinen Abschluss an
          die untergeordneten Vorgaenge weiter. */}
      <label className="field__checkbox">
        <input
          type="checkbox"
          checked={majorIncident}
          onChange={(event) => {
            setMajorIncident(event.target.checked);
          }}
        />
        {t('tickets.majorIncidentHint')}
      </label>

      {update.isError ? <ErrorNotice error={update.error} /> : null}

      <div className="row">
        <Button type="submit" variant="primary" busy={update.isPending}>
          {t('app.save')}
        </Button>
        {update.isSuccess ? (
          <span className="field__hint" role="status">
            {t('tickets.saved')}
          </span>
        ) : null}
      </div>
    </form>
  );
}

interface SelectionProps {
  id: string;
  label: string;
  value: string;
  options: { id: string; label: string }[];
  emptyLabel?: string;
  onChange: (value: string) => void;
}

function Selection({ id, label, value, options, emptyLabel, onChange }: SelectionProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <Select
        id={id}
        className="field__control"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {emptyLabel ? <option value="">{emptyLabel}</option> : null}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
